<template>
  <div id="app" class="sfdocs">
    <router-view />
  </div>
</template>

<script>
import BroadcastChannel from 'broadcast-channel';
import { mapActions, mapState, mapMutations } from 'vuex';
import { Iot } from 'aws-sdk';
import Amplify from 'aws-amplify';
import { AWSIoTProvider } from '@aws-amplify/pubsub/lib/Providers';

export default {
  components: {},
  data() {
    return {
      loginListener: null,
      wasOffline: false
    };
  },
  methods: {
    ...mapActions([
      'getCognitoCredentials',
      'getLoginState',
      'setupDirectoriesAndStructure'
    ]),
    ...mapMutations(['SET_OBJECT_TYPE']),
    async attachPolicy() {
      const credentials = await this.$Amplify.Auth.currentCredentials();

      // create IoT service
      const iot = new Iot({
        region: 'us-east-1',
        credentials: this.$Amplify.Auth.essentialCredentials(credentials)
      });

      // get policy details
      const policyName = process.env.VUE_APP_IOT_POLICY;
      const target = credentials._identityId;

      // get policies currently attached to Cognito user
      const { policies } = await iot.listAttachedPolicies({ target }).promise();

      // if current user doesn't have the policy, add it
      if (!policies.find((policy) => policy.policyName === policyName)) {
        console.log(`attaching policy ${policyName} to target ${target}`);
        await iot.attachPolicy({ policyName, target }).promise();
      }
    },
    async setup() {
      await this.getLoginState();
      // try to restore last location from the field in localStorage
      const lastString = window.localStorage.getItem('lastRecord');
      if (lastString !== null) {
        const lastRecord = JSON.parse(lastString);

        if (lastRecord !== null) {
          const now = Date.now();

          if (now - new Date(lastRecord.set) < 120000) {
            /* if the value was set less than 2 minutes ago, use it for the current URL
                   if there are no route params */
            window.localStorage.removeItem('lastRecord');
            this.$router.push({
              name: 'folder-view',
              params: {
                orgSalesforceId: lastRecord.route.orgSalesforceId,
                recordType: lastRecord.route.recordType,
                recordTypeSalesforceId: lastRecord.route.recordTypeSalesforceId
              },
              query: {
                info: lastRecord.route.info
              }
            });
          } else {
            // remove record if it was stored longer than 2 minutes ago
            window.localStorage.removeItem('lastRecord');
          }
        }
      }
    }
  },
  computed: {
    ...mapState(['loggedIn', 'slideoutOpen', 'slideoutFile'])
  },
  async mounted() {
    // hack to make it look like this window was opened by a script
    window.open('', '_self', '');
    // open communication channel so this window can close itself when a newer one is opened
    const channel = new BroadcastChannel('shouldClose');
    // post message before starting to listen for close events
    channel.postMessage('close');
    channel.addEventListener('message', () => {
      window.close();
    });

    try {
      await this.setup();
      await this.attachPolicy();
      // get IoT endpoint
      const { endpoint } = await this.$Amplify.API.get('API', '/iot');

      // add IoT plugin to Amplify
      Amplify.addPluggable(
        new AWSIoTProvider({
          aws_pubsub_region: 'us-east-1',
          aws_pubsub_endpoint: endpoint
        })
      );
    } catch (e) {
      console.log(e);
    }

    // reload page when it goes from offline -> online, as this breaks the IoT WebSocket
    window.addEventListener('online', () => {
      if (this.wasOffline) {
        window.location.reload();
      }
    });
    window.addEventListener('offline', () => {
      this.wasOffline = true;
    });
  }
};
</script>

<style lang="scss">
@import './assets/scss/main';

#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}
#nav {
  padding: 30px;
  a {
    font-weight: bold;
    color: #2c3e50;
    &.router-link-exact-active {
      color: #42b983;
    }
  }
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 999;
}

.hidden {
  display: none !important;
}
</style>
